import { Save, ShieldCheck } from "lucide-react";
import React, { useEffect, useId, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import usersService, { User } from "../services/usersService";
import { USER_PERMISSION_GROUPS, canManageUserPermissions, scopesForRole, validateUserPermissions } from "../utils/userPermissions";
import httpService from "../services/httpService";
import type { HttpError } from "../services/httpService";
import AlphaSettingsModal from "../pages/alpha/AlphaSettingsModal";
import { alphaAdminUi } from "./admin/alphaAdminUi";

interface Provider {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface UserPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
  mode?: "create" | "edit";
  initialRoles?: string[];
  onSave: (userData: Partial<User>) => Promise<void>;
}

const UserPermissionsModal: React.FC<UserPermissionsModalProps> = ({ isOpen, onClose, user, mode = "edit", initialRoles, onSave }) => {
  const { user: actor } = useAuth();
  const titleId = useId();
  const selection = isOpen ? `${actor?.id ?? ""}:${mode}:${user?.id ?? "new"}` : null;
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const requestRef = useRef(0);
  const mutationRef = useRef(0);
  const [formData, setFormData] = useState({
    roles: ["INVESTOR"] as string[], scopes: [] as string[], providerId: "",
  });
  const [initialUser, setInitialUser] = useState<User | null>(null);
  const [loadedSelection, setLoadedSelection] = useState<string | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mayManagePermissions = () => isOpen && canManageUserPermissions(actor,
    actor?.roles?.includes("ADMIN") ? httpService.getAuthTokenPair()?.accessToken ?? null : null);
  const canEditPermissions = mayManagePermissions();
  const permissionAccessRef = useRef(canEditPermissions);
  permissionAccessRef.current = canEditPermissions;
  const isReady = mode === "create" || loadedSelection === selection;
  const permissionError = mode === "edit" && canEditPermissions
    ? validateUserPermissions(formData.roles, formData.scopes)
    : mode === "create" && !formData.roles.length ? "Select at least one role." : null;
  const controlsDisabled = loading || dataLoading || !isReady || !canEditPermissions;
  const isSaveDisabled = controlsDisabled || !!permissionError
    || (mode === "edit" && formData.roles.includes("PROVIDER") && !formData.providerId);

  useEffect(() => {
    if (!isOpen) return;
    const requestId = ++requestRef.current;
    let active = true;
    const invalidatePendingMutation = () => { ++mutationRef.current; };
    const owner = selection;
    const isCurrent = () => active && requestRef.current === requestId && selectionRef.current === owner;
    setLoading(false);
    setError(null);
    setInitialUser(null);
    setLoadedSelection(null);
    setProviders([]);
    setFormData({ roles: mode === "create" ? [...(initialRoles ?? ["INVESTOR"])] : [], scopes: [], providerId: "" });
    setDataLoading(mode === "edit");

    if (mode === "edit" && user) {
      usersService.getUserById(user.id).then(detail => {
        if (!isCurrent()) return;
        if (!Array.isArray(detail.scopes) || !Array.isArray(detail.roles)) {
          throw new Error("Saved roles and scopes are missing. Reload the user before editing.");
        }
        setInitialUser(detail);
        setFormData({ roles: [...detail.roles], scopes: [...detail.scopes],
          providerId: String(detail.provider?.id ?? detail.providerId ?? "") });
        setLoadedSelection(owner);
      }).catch((err: unknown) => {
        if (!isCurrent()) return;
        setError(err instanceof Error ? err.message : "Unable to load saved roles and scopes.");
      }).finally(() => {
        if (isCurrent()) setDataLoading(false);
      });
    }
    if (mode === "edit" && permissionAccessRef.current) {
      httpService.get<{ providers: Provider[] }>("/providers", { requestScope: "actor" }).then(response => {
        if (isCurrent()) setProviders((response.data.providers ?? []).map(provider => ({ ...provider, id: String(provider.id) })));
      }).catch(() => {
        if (isCurrent()) setProviders([]);
      });
    }
    return () => { active = false; invalidatePendingMutation(); };
  }, [isOpen, mode, user, selection, reload, initialRoles]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaveDisabled) return;
    if (!mayManagePermissions()) {
      setError("Only an administrator with full administration access can change roles and scopes.");
      return;
    }
    const validation = mode === "edit" ? validateUserPermissions(formData.roles, formData.scopes) : null;
    if (validation) { setError(validation); return; }
    const mutationId = ++mutationRef.current;
    const owner = selection;
    const isCurrent = () => mutationRef.current === mutationId && selectionRef.current === owner;
    setLoading(true);
    setError(null);
    try {
      await onSave({ roles: formData.roles,
        ...(mode === "edit" ? { scopes: formData.scopes } : {}),
        ...(mode === "edit" && formData.providerId !== String(initialUser?.provider?.id ?? initialUser?.providerId ?? "")
          ? { providerId: formData.providerId } : {}),
      });
      if (isCurrent()) onClose();
    } catch (err: unknown) {
      if (!isCurrent()) return;
      const denied = (err as HttpError | null)?.status === 403 || (err as HttpError | null)?.response?.status === 403;
      setError(denied ? "You do not have permission to save this user. Your draft has been kept."
        : err instanceof Error ? err.message : "Unable to save this user.");
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  return (
    <AlphaSettingsModal isOpen={isOpen} onClose={onClose} title="Roles and scopes" closeDisabled={loading}
      maxWidthClass="max-w-2xl" spacious
      description={mode === "edit" ? user?.email : "Choose the new user's roles. Default scopes are assigned at creation."}>
      <form onSubmit={handleSubmit} aria-label="User permissions" className="space-y-5">
        <div className="flex items-start gap-3 rounded-v7-panel bg-v7-paper p-4 text-sm text-v7-ink-soft">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-v7-bronze-dark" aria-hidden="true" />
          <p>{mode === "edit" ? "Choose a role and its access together. Selecting a role does not automatically grant full access." : "Choose roles for this account. Each role receives its default access when the account is created."}</p>
        </div>
        {dataLoading && <p role="status" className="text-sm text-v7-ink-soft">Loading saved roles and scopes...</p>}
        {!dataLoading && !isReady && <button type="button" onClick={() => setReload(value => value + 1)} className={alphaAdminUi.link}>Reload user</button>}
        {error && <p role="alert" className={alphaAdminUi.error}>{error}</p>}
        <fieldset disabled={controlsDisabled} className="space-y-3">
          <legend className="sr-only">Roles and scopes</legend>
          {!canEditPermissions && <p className="text-sm text-v7-ink-soft">Only administrators with full administration access can edit roles and scopes.</p>}
          {mode === "edit" && isReady && initialUser?.scopes?.length === 0 && <p className="text-sm text-v7-ink-soft">No scopes are saved for this user.</p>}
          {mode === "edit" && isReady && !!initialUser?.scopes?.length && <p className="break-words text-xs text-v7-ink/60">Saved scopes: {initialUser.scopes.join(", ")}</p>}
          {USER_PERMISSION_GROUPS.map(group => {
            const selected = isReady && formData.roles.includes(group.role);
            return <div key={group.role} className="rounded-v7-panel border border-v7-ink/15 bg-v7-white p-4">
              <label className="flex items-center gap-3 text-sm font-semibold text-v7-ink">
                <input type="checkbox" disabled={controlsDisabled} checked={selected} className="h-4 w-4 shrink-0 accent-v7-bronze-dark focus:ring-v7-bronze-dark"
                  onChange={event => {
                    if (!mayManagePermissions()) return;
                    const checked = event.target.checked;
                    setFormData(previous => ({ ...previous,
                      roles: checked ? [...previous.roles, group.role] : previous.roles.filter(role => role !== group.role),
                      scopes: checked ? previous.scopes : previous.scopes.filter(scope => !scopesForRole(group.role).includes(scope)),
                      providerId: !checked && group.role === "PROVIDER" ? "" : previous.providerId,
                    }));
                  }} />
                {group.label}
              </label>
              {mode === "edit" && selected && <div className="mt-3 grid gap-3 sm:grid-cols-2 border-t border-v7-ink/10 pt-3">
                {group.scopes.map(scope => <label key={scope.value} className="flex items-start gap-3 text-sm text-v7-ink-soft">
                  <input type="checkbox" disabled={controlsDisabled} checked={formData.scopes.includes(scope.value)}
                    aria-label={scope.value} className="mt-0.5 h-4 w-4 shrink-0 accent-v7-bronze-dark focus:ring-v7-bronze-dark"
                    onChange={event => {
                      if (!mayManagePermissions()) return;
                      const checked = event.target.checked;
                      setFormData(previous => ({ ...previous, scopes: checked ? [...previous.scopes, scope.value] : previous.scopes.filter(value => value !== scope.value) }));
                    }} />
                  <span className="min-w-0"><span className="block">{scope.label}</span><span className="block break-all font-mono text-xs text-v7-ink/60">{scope.value}</span></span>
                </label>)}
              </div>}
            </div>;
          })}
        </fieldset>
        {mode === "edit" && <p className="text-xs text-v7-ink/60">Each selected role needs at least one matching scope. Write scopes include reading that area; grants combine additively. Changes apply on the next login or token refresh. Existing tokens keep their scopes until expiry. Review-account restrictions still apply.</p>}
        {mode === "create" && <p className="text-xs text-v7-ink/60">New users receive the default access for each selected role. After creation, open Roles and scopes to choose narrower access.</p>}
        {permissionError && isReady && <p role="alert" className="text-sm text-v7-rust">{permissionError}</p>}

        {/* Provider Selection - Only show if PROVIDER role is selected */}
        {mode === "edit" && isReady && formData.roles.includes("PROVIDER") && (
          <div>
            <label htmlFor={`${titleId}-provider`} className="mb-1 block text-sm font-medium text-v7-ink">
              Provider
            </label>
            <select
              id={`${titleId}-provider`}
              value={formData.providerId}
              disabled={controlsDisabled}
              onChange={e => setFormData({ ...formData, providerId: e.target.value })}
              className={alphaAdminUi.input}
            >
              <option value="">Select a provider</option>
              {formData.providerId && !providers.some(provider => provider.id === formData.providerId) && <option value={formData.providerId}>{initialUser?.provider?.name ?? formData.providerId}</option>}
              {providers.map(provider => (
                <option key={provider.id} value={provider.id}>
                  {provider.name}
                </option>
              ))}
            </select>
            {providers.length === 0 && (
              <p className="mt-1 text-sm text-v7-ink/60">
                No providers available. Create a provider first.
              </p>
            )}
            {formData.roles.includes("PROVIDER") && !formData.providerId && providers.length > 0 && (
              <p className="mt-1 text-sm text-v7-rust">
                Please select a provider to continue
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-3 border-t border-v7-ink/10 pt-4">
          <button type="button" disabled={loading} onClick={onClose} className={alphaAdminUi.secondary}>Cancel</button>
          <button type="submit" disabled={isSaveDisabled} className={alphaAdminUi.primary}>
            <Save className="h-4 w-4" aria-hidden="true" />{loading ? "Saving..." : mode === "create" ? "Use roles" : "Save Changes"}
          </button>
        </div>
      </form>
    </AlphaSettingsModal>
  );
};

export default UserPermissionsModal;
